import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { employeesApi, type EmployeePickerItem } from '../lib/api/employees';
import { formatVND } from '../lib/format';

export interface RecipientEntry {
  employeeId: string;
  employeeName: string;
  employeeRole?: string;
  amount: number;
  note?: string;
}

export type BonusMode = 'equal' | 'manual';

interface Props {
  totalAmount: number;
  recipients: RecipientEntry[];
  mode: BonusMode;
  onChange: (recipients: RecipientEntry[]) => void;
  onModeChange: (mode: BonusMode) => void;
}

export function BonusRecipientPicker({
  totalAmount,
  recipients,
  mode,
  onChange,
  onModeChange,
}: Props) {
  const [allEmployees, setAllEmployees] = useState<EmployeePickerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    employeesApi
      .picker()
      .then(setAllEmployees)
      .catch(() => setAllEmployees([]))
      .finally(() => setLoading(false));
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    if (!searchOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [searchOpen]);

  const selectedIds = useMemo(() => new Set(recipients.map((r) => r.employeeId)), [recipients]);
  const suggestions = useMemo(() => {
    const s = search.toLowerCase();
    return allEmployees
      .filter((e) => !selectedIds.has(e.id))
      .filter(
        (e) =>
          !s ||
          e.name.toLowerCase().includes(s) ||
          (e.email || '').toLowerCase().includes(s) ||
          (e.code || '').toLowerCase().includes(s) ||
          (e.position || '').toLowerCase().includes(s),
      )
      .slice(0, 8);
  }, [allEmployees, selectedIds, search]);

  const recalcEqual = useCallback(
    (list: RecipientEntry[]) => {
      if (list.length === 0) return list;
      const each = Math.floor(totalAmount / list.length);
      const remainder = totalAmount - each * list.length;
      return list.map((r, i) => ({
        ...r,
        amount: i === list.length - 1 ? each + remainder : each,
      }));
    },
    [totalAmount],
  );

  useEffect(() => {
    if (mode !== 'equal') return;
    if (recipients.length === 0) return;
    const next = recalcEqual(recipients);
    const changed = next.some((r, i) => r.amount !== recipients[i].amount);
    if (changed) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAmount, mode, recipients.length]);

  const addRecipient = (emp: EmployeePickerItem) => {
    const entry: RecipientEntry = {
      employeeId: emp.id,
      employeeName: emp.name,
      employeeRole: emp.position || emp.departmentName || '',
      amount: 0,
    };
    const next = [...recipients, entry];
    onChange(mode === 'equal' ? recalcEqual(next) : next);
    setSearch('');
  };

  const removeRecipient = (id: string) => {
    const next = recipients.filter((r) => r.employeeId !== id);
    onChange(mode === 'equal' ? recalcEqual(next) : next);
  };

  const updateAmount = (id: string, amount: number) => {
    onChange(recipients.map((r) => (r.employeeId === id ? { ...r, amount } : r)));
  };

  const manualSum = recipients.reduce((s, r) => s + r.amount, 0);
  const equalPer = recipients.length > 0 ? Math.floor(totalAmount / recipients.length) : 0;

  return (
    <div className="brp-card">
      {/* Header */}
      <div className="brp-header">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round">
          <path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
        </svg>
        <span>THƯỞNG NÓNG · CHI CHO DANH SÁCH NV</span>
      </div>

      {/* Mode selector */}
      <div className="brp-modes">
        <button
          type="button"
          className={`brp-mode ${mode === 'equal' ? 'active' : ''}`}
          onClick={() => onModeChange('equal')}
        >
          <div className="brp-mode-head">
            <div className={`brp-radio ${mode === 'equal' ? 'checked' : ''}`} />
            <span>Chia đều từ Số tiền</span>
          </div>
          <div className="brp-mode-desc">Số tiền ở form trên chia đều cho các NV được chọn</div>
        </button>
        <button
          type="button"
          className={`brp-mode ${mode === 'manual' ? 'active' : ''}`}
          onClick={() => onModeChange('manual')}
        >
          <div className="brp-mode-head">
            <div className={`brp-radio ${mode === 'manual' ? 'checked' : ''}`} />
            <span>Nhập tay từng người</span>
          </div>
          <div className="brp-mode-desc">Nhập số tiền riêng cho từng NV (thưởng theo mức độ đóng góp)</div>
        </button>
      </div>

      {/* Search + suggestions */}
      <div className="brp-field">
        <label>Thêm NV nhận thưởng</label>
        <div className="brp-search-wrap" ref={searchWrapRef}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setSearchOpen(true)}
            placeholder="Tìm NV theo tên/chức vụ để thêm..."
          />
          {searchOpen && (
            <div className="brp-suggestions">
              {loading ? (
                <div className="brp-sugg-empty">Đang tải…</div>
              ) : suggestions.length === 0 ? (
                <div className="brp-sugg-empty">
                  {allEmployees.length === 0 ? 'Không có NV (chưa sync HR?)' : 'Không tìm thấy'}
                </div>
              ) : (
                suggestions.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    className="brp-sugg-row"
                    onClick={() => addRecipient(e)}
                  >
                    <div className="brp-avatar">{initials(e.name)}</div>
                    <div className="brp-sugg-meta">
                      <div className="brp-sugg-name">{e.name}</div>
                      <div className="brp-sugg-role">{e.position || e.departmentName || '—'}</div>
                    </div>
                    <span className="brp-sugg-add">+ Thêm</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Summary bar */}
      {recipients.length > 0 && (
        <div className="brp-summary">
          <span className="brp-summary-count">
            Đã chọn <b>{recipients.length}</b> NV
          </span>
          {mode === 'equal' ? (
            <span className="brp-summary-info">
              Mỗi người: <b>{formatVND(equalPer)}</b>{' '}
              <small>(auto từ Số tiền)</small>
            </span>
          ) : (
            <span className={`brp-summary-info ${manualSum === totalAmount ? 'ok' : 'warn'}`}>
              Tổng: <b>{formatVND(manualSum)}</b>
              {manualSum !== totalAmount && (
                <small style={{ color: '#b91c1c', marginLeft: 6 }}>
                  {manualSum > totalAmount ? 'thừa' : 'thiếu'} {formatVND(Math.abs(totalAmount - manualSum))}
                </small>
              )}
            </span>
          )}
        </div>
      )}

      {/* Selected list */}
      {recipients.length === 0 ? (
        <div className="brp-empty">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="9" cy="7" r="4" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          </svg>
          <div>Chưa có NV nào được chọn. Search + Add ở trên để thêm.</div>
        </div>
      ) : (
        <div className="brp-list">
          {recipients.map((r) => (
            <div key={r.employeeId} className="brp-row">
              <div className="brp-avatar">{initials(r.employeeName)}</div>
              <div className="brp-row-meta">
                <div className="brp-row-name">{r.employeeName}</div>
                {r.employeeRole && <div className="brp-row-role">{r.employeeRole}</div>}
              </div>
              {mode === 'manual' ? (
                <input
                  type="text"
                  inputMode="numeric"
                  className="brp-row-input"
                  value={r.amount ? r.amount.toLocaleString('vi-VN') : ''}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    updateAmount(r.employeeId, digits ? Number(digits) : 0);
                  }}
                  placeholder="0"
                />
              ) : (
                <span className="brp-row-amount">{formatVND(r.amount)}</span>
              )}
              <button
                type="button"
                className="brp-row-remove"
                onClick={() => removeRecipient(r.employeeId)}
                title="Bỏ NV này khỏi list"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

function initials(name: string): string {
  return (name || '?')
    .split(' ')
    .slice(-2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}
